import { autoinject, bindingMode, observable } from 'aurelia-framework';
import { ComponentBase, Sorting, SortDirection } from 'app/shared/lib/essette-site';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { INotesConfig } from 'app/core/models/essette-interfaces';
import { AuthService } from 'app/um/services/auth-service';
import { apiCall } from 'app/shared/lib/api/api';
import { Toaster } from 'app/shared/lib/toaster';
import { bindable } from 'aurelia-typed-observable-plugin';
import { NoteService } from 'app/core/services/note-service';
import { getReference, getReferences } from 'app/shared/models/essette-types';
import { AuthPortalPageContext } from 'app/pp/lib/auth-portal-page-context';
import { IWizardStep } from 'app/core/components/wizard/interfaces';
import { fireEvents } from 'app/shared/lib/fire-events';
import { focusFirst } from 'app/shared/lib/focus';
import { EnvironmentService } from 'app/shared/services/environment-service';
import { I18N } from 'aurelia-i18n';
import { IServiceGridOptions } from 'app/um/components/auth/service-grid';
import { IAuthMedicationsGridOptions } from 'app/um/components/auth/medications/auth-medications-grid';
import { ISaveAuthMedicationOptions } from 'app/um/components/auth/medications/save-auth-medication';
import { ISaveServiceOptions } from 'app/um/components/auth/save-service';
import { validate } from 'app/shared/elements/ess-rule';
import { ExistingContact } from 'app/pp/components/submit/existing-contact';
import { LookupService } from 'app/cd/services/lookup-service';
import { naturalSort } from 'app/shared/lib/arrays';
import * as CD from 'app/cd/models/essette-api';
import * as UM from 'app/um/models/essette-api';

@autoinject()
export class AuthDetailsStep extends ComponentBase implements IWizardStep {

    ////****************** Fields and Properties ************************************////
    @bindable.object({ defaultBindingMode: bindingMode.twoWay, changeHandler: 'reload' })
    protected auth: UM.Auth = {};

    @observable
    protected chiefComplaintWatcher: UM.AuthChiefComplaint | null = null;

    @bindable.boolean()
    protected readonly: boolean = false;

    @bindable.object()
    protected missingRequiredData: string[] = [];

    protected existingContact: CD.ProviderContact = {};

    public canSkip: boolean = false;
    public canContinue: boolean = false;
    public canPerformFinalSubmit: boolean = false;
    public hasPreferredContact: boolean = false;

    protected validate = validate;

    protected additionalInfo: UM.AuthNote | null = null;
    protected noteCategory: UM.AuthNoteCategory | null = null;

    @observable
    protected preferredContactOption: string = 'existing';

    protected editContact: UM.AuthPreferredContact = {};

    protected notesConfig: INotesConfig = {
        serviceRootUrl: 'um/auths',
        targetType: 'UM.Auth',
        moduleCode: 'UM',
        addTargetProperty: this.addAuthProperty.bind(this)
    }

    protected priorities: UM.AuthPriority[] = [];
    protected admissionSources: UM.AdmissionSource[] = [];
    protected healthcareServiceTypes: UM.HealthcareServiceType[] = [];
    protected placesOfService: CD.PlaceOfService[] = [];

    // Additional Fields
    protected $additionalFieldIsValid: boolean = false;
    protected hasMultipleAdditionalFields: boolean = false
    @observable
    protected selectedPriority: UM.AuthPriority | null = null;
    @observable
    protected selectedAdmissionSource: UM.AdmissionSource | null = null;
    @observable
    protected selectedHealthcareServiceType: UM.HealthcareServiceType | null = null;
    @observable
    protected selectedPlaceOfService: CD.PlaceOfService | null = null;

    protected infoUnsaved = true;

    protected canSearchNonContracted: boolean = false;

    protected additionalInfoLabel: string = '';

    protected serviceGridOptions: IServiceGridOptions = {
        canOpenServiceStatusHistory: false,
        canSyncToClaimsSystems: false,
        showDueDate: false,
        showServiceStatusReason: false,
        canApplyBulkServiceStatus: false,
        canApplyCorrespondenceRules: false,
        showExternalActions: false,
        showApprovedWithRequestedCharges: false,
        canSyncToClaimsSystemsLineLevel: false,
        showExistingAuthsWarning: false,
        showDuplicateAuths: false,
        showAddDocumentButton: false,
        showOpenIqcSummary: false,
        showWarnings: true,
        showModifiers: false,
        showQuantityApproved: false
    };

    protected dentalServiceGridOptions: IServiceGridOptions = {
        canOpenServiceStatusHistory: false,
        canSyncToClaimsSystems: false,
        showDueDate: false,
        showServiceStatusReason: false,
        canApplyBulkServiceStatus: false,
        canApplyCorrespondenceRules: false,
        showExternalActions: false,
        showApprovedWithRequestedCharges: true,
        canSyncToClaimsSystemsLineLevel: false,
        showExistingAuthsWarning: false,
        showDuplicateAuths: false,
        showAddDocumentButton: false,
        showOpenIqcSummary: false,
        showWarnings: true,
        showModifiers: false,
        showQuantityApproved: false
    };

    protected saveServiceOptions: ISaveServiceOptions = {
        showStatus: false,
        showStatusReason: false,
        showQtyApproved: false,
        showApprovedCharges: false,
        showModifiers: false
    };

    protected saveDentalServiceOptions: ISaveServiceOptions = {
        showStatus: false,
        showStatusReason: false,
        showQtyApproved: false,
        showApprovedCharges: false,
        showModifiers: false
    };

    protected authMedicationsGridOptions: IAuthMedicationsGridOptions = {
        showStatusReason: false,
        showQtyApproved: false,
        showDaysApproved: false,
        showApprovedWithRequestedCharges: false,
        showWarnings: true
    };

    protected saveAuthMedicationOptions: ISaveAuthMedicationOptions = {
        showStatusReason: false,
        showQuantityApproved: false,
        showUnitApproved: false,
        showDaysApproved: false,
        showApprovedCharges: false
    };

    ////****************** Element References ***************************************////

    protected $existingContact!: ExistingContact;
    protected $authAdditionalFieldsElement!: Element;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        protected readonly env: EnvironmentService,
        protected readonly context: AuthPortalPageContext,
        private readonly authService: AuthService,
        private readonly lookupService: LookupService,
        private readonly toaster: Toaster,
        private readonly noteService: NoteService,
        private readonly $element: Element,
        protected readonly i18n: I18N,
        ...rest: any[]) {
        super(...rest);
        this.noteService.configure(this.notesConfig);
        this.additionalInfoLabel = this.env.config.labels?.pp?.additionalInfo || this.i18n.tr('um:auth.additionalInfo.$long');
        this.checkPermissions();
    }

    protected bind(): void {
        this.reload();
    }

    ////****************** Events ***************************************************////

    protected preferredContactOptionChanged() {
        // Clear editContact on radial Preferred Contact option change
        this.editContact = {};
        if(this.$existingContact) this.$existingContact.reset();
    }

    protected async selectedPriorityChanged(newValue: UM.AuthPriority, oldValue: UM.AuthPriority): Promise<void> {
        if(!this.auth.id || this.hasMultipleAdditionalFields) return;
        // NOTE: [EN] priorityChanged is called multiple times upon load. Logic check if auth has loaded.

        if((this.auth.priority && newValue && newValue.id !== this.auth.priority.id) || !this.auth.priority || !newValue) {
            // NOTE: [EN] In API, auth priority can be null. Check if auth's priority is not the same as new priority or others are null.
            const auth: UM.Auth = {
                id: this.auth.id,
                priority: getReference(newValue)
            };

            await this.saveAdditionalFields(auth);
            this.auth.priority = newValue;
        }
    }

    protected async selectedAdmissionSourceChanged(newValue: UM.AdmissionSource, oldValue: UM.AdmissionSource): Promise<void> {
        if(!this.auth.id || this.hasMultipleAdditionalFields) return;
        if((this.auth.admission?.source && newValue && newValue.id !== this.auth.admission.source.id) || !this.auth.admission?.source || !newValue) {
            const auth: UM.Auth = {
                id: this.auth.id,
                admission: {
                    source: getReference(newValue)
                }
            };

            await this.saveAdditionalFields(auth);
            if(!this.auth.admission) this.auth.admission = {};
            this.auth.admission.source = newValue;
        }
    }

    protected async selectedHealthcareServiceTypeChanged(newValue: UM.HealthcareServiceType, oldValue: UM.HealthcareServiceType): Promise<void> {
        if(!this.auth.id || this.hasMultipleAdditionalFields) return;
        if((this.auth.healthcareServiceType && newValue && newValue.id !== this.auth.healthcareServiceType.id) || !this.auth.healthcareServiceType || !newValue) {
            const auth: UM.Auth = {
                id: this.auth.id,
                healthcareServiceType: getReference(newValue)
            };

            await this.saveAdditionalFields(auth);
            this.auth.healthcareServiceType = newValue;
        }
    }

    protected async selectedPlaceOfServiceChanged(newValue: CD.PlaceOfService, oldValue: CD.PlaceOfService): Promise<void> {
        if(!this.auth.id || this.hasMultipleAdditionalFields) return;
        if((this.auth.placeOfService && newValue && newValue.id !== this.auth.placeOfService.id) || !this.auth.placeOfService || !newValue) {
            const auth: UM.Auth = {
                id: this.auth.id,
                placeOfService: getReference(newValue)
            };

            await this.saveAdditionalFields(auth);
            this.auth.placeOfService = newValue;
        }
    }

    ////****************** Functions ************************************************////
    public skipStep(): void { }
    public continueStep(): void { }
    public openStep(): void {
        if(!this.readonly)
            setTimeout(() => focusFirst(this.$element), 250); //priority

        // Perform an initial validation in case there are no rule/validations found
        if(this.$authAdditionalFieldsElement) {
            this.$additionalFieldIsValid = validate(this.$authAdditionalFieldsElement);
        }
    }
    public async closeStep(): Promise<void> {
        await this.closeStepActions();
    }
    public async finalSubmit(): Promise<void> { }

    private addAuthProperty(note: UM.AuthNote): void {
        note.auth = { id: this.auth?.id };
    }

    private async reload(): Promise<void> {
        this.serviceGridOptions.showModifiers = this.saveServiceOptions.showModifiers = this.context?.settings?.servicesModifier?.visible ?? false;
        this.dentalServiceGridOptions.showModifiers = this.saveDentalServiceOptions.showModifiers = this.context?.settings?.servicesDentalModifier?.visible ?? false;

        this.loadNoteCategory().then(() => this.loadAdditionalInfo());
        this.loadLookups();
        this.selectedPriority = this.auth.priority || null;
        this.selectedAdmissionSource = this.auth.admission?.source || null;
        this.selectedHealthcareServiceType = this.auth.healthcareServiceType || null;
        this.selectedPlaceOfService = this.auth.placeOfService || null;
        this.chiefComplaintWatcher = this.auth.chiefComplaint || null;
        this.canContinue = this.canContinueToNextStep();
        // Loading Context settings based on auth
        this.serviceGridOptions.showModifiers = this.saveServiceOptions.showModifiers = this.context?.settings?.servicesModifier?.visible ?? false;
        this.dentalServiceGridOptions.showModifiers = this.saveDentalServiceOptions.showModifiers = this.context?.settings?.servicesDentalModifier?.visible ?? false;
        // Additional Fields, check if more than one are enabled
        const enabledAdditionalFields = [this.context.settings.priority.visible, this.context.settings.admissionSource.visible, this.context.settings.healthcareServiceType.visible, this.context.settings.placeOfService.visible];
        this.hasMultipleAdditionalFields = enabledAdditionalFields.filter(fieldState => fieldState == true).length > 1;
    }

    protected chiefComplaintWatcherChanged(newValue: UM.AuthChiefComplaint | null, oldValue: UM.AuthChiefComplaint | null): void {
        if(this.context && this.context.settings && this.context.settings.chiefComplaint && this.context.settings.chiefComplaint.reqDataEntry) {
            if(!Array.isArray(this.missingRequiredData)) {
                this.missingRequiredData = [];
            }
            const list: string[] = this.missingRequiredData as string[];

            const fieldKey = 'um:auth.chiefComplaint.$';
            const translated = this.i18n.tr(fieldKey);
            const isValid = !!(newValue && newValue.text && newValue.text.trim().length > 0);

            const foundIndexKey = list.findIndex(s => s === fieldKey);
            const foundIndexTranslated = list.findIndex(s => s === translated);

            if(isValid) {
                if(foundIndexKey > -1) list.splice(foundIndexKey, 1);
                if(foundIndexTranslated > -1) list.splice(foundIndexTranslated, 1);
            }
            else {
                if(foundIndexKey < 0 && foundIndexTranslated < 0) list.push(fieldKey);
            }
            this.canContinue = list.length === 0;
        }
    }

    private canContinueToNextStep(): boolean {
        let preferredContactPass = true;
        if(this.hasPreferredContact && !this.auth?.preferredContact?.id) {
            preferredContactPass = false;
            this.context.missingRequiredForDetermination.push('um:authPreferredContact.$');
        }

        return this.missingRequiredData.length === 0;
    }

    protected handleUserInput(event: Event): boolean {
        this.infoUnsaved = true;
        return true; // don't prevent default
    }

    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.canSearchNonContracted = !!this.accountContext.hasPermission('CD.ProviderLocation.Query.SearchNonContracted');
        this.hasPreferredContact = !!this.accountContext.hasPermission('UM.Auth.Update.PreferredContact');
    }

    protected buildContactWithProviderContact(): UM.AuthPreferredContact {
        const contact: UM.AuthPreferredContact = {
            auth: getReference(this.auth),
            name: this.existingContact.name?.full,
            emailAddress: this.existingContact.emailAddress ? this.existingContact.emailAddress : undefined,
            phone: this.existingContact.phoneNumber?.primary?.number ?
                {
                    number: this.existingContact.phoneNumber.primary.number,
                    extension: this.existingContact.phoneNumber.primary.extension,
                } : undefined,
            fax: {
                number: this.existingContact.phoneNumber?.fax?.number,
                extension: this.existingContact.phoneNumber?.fax?.extension
            }
        };

        return contact;
    }

    protected buildContact(): UM.AuthPreferredContact {
        const contact: UM.AuthPreferredContact = {
            auth: getReference(this.auth),
            name: this.editContact.name,
            emailAddress: this.editContact.emailAddress && this.editContact.emailAddress.trim().length > 0 ? this.editContact.emailAddress : undefined,
            phone: this.editContact.phone?.number ?
                {
                    number: this.editContact.phone.number,
                    extension: this.editContact.phone?.extension && this.editContact.phone.extension.trim().length > 0 ? this.editContact.phone.extension : null,
                } : undefined,
            fax: this.editContact.fax?.number ?
                {
                    number: this.editContact.fax.number,
                    extension: this.editContact.fax?.extension && this.editContact.fax.extension.trim().length > 0 ? this.editContact.fax.extension : null,
                } : undefined
        }

        return contact;
    }

    protected resetPreferredContact(contactSaved: boolean = false): void {
        if(this.$existingContact) {
            // if contact is saved, resetting Existing Contact component should not reselect any previous selections
            this.$existingContact.contactSaved = contactSaved;
            this.$existingContact.reset();
        }
        this.existingContact = {};
        this.editContact = {};
        this.preferredContactOption = 'existing';
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    protected async saveChiefComplaint(): Promise<void> {
        if(!this.auth.id) { return; }

        const chiefComplaint: UM.AuthChiefComplaint = {
            auth: { id: this.auth.id },
            text: this.auth.chiefComplaint ? (this.auth.chiefComplaint.text && this.auth.chiefComplaint.text.length > 0 ? this.auth.chiefComplaint.text : null) : null
        } as UM.AuthChiefComplaint;

        let isSuccessful = false;
        try {
            this.state.beginSave();

            const response = await this.authService.saveChiefComplaint(chiefComplaint);
            isSuccessful = response.isSuccessful;
            if(isSuccessful) {
                this.auth.chiefComplaint = { text: chiefComplaint.text } as UM.AuthChiefComplaint;
            }
            const detail = { authReloaded: false, triggerRules: true };
            fireEvents(this.$element, ['auth-change'], detail);
        }
        catch(error) {
            throw error;
        }
        finally {
            this.toaster.standard.saved('um:auth.chiefComplaint.$', isSuccessful);
            this.state.endSave();
        }
    }

    @apiCall
    protected async saveAdditionalInfo(): Promise<void> {
        if(!this.auth.id || !this.additionalInfo || !this.noteCategory) { return; }

        const saveNote: UM.AuthNote = {
            id: this.additionalInfo.id,
            text: this.additionalInfo.text,
            category: getReference(this.noteCategory),
            flags: getReferences(this.noteCategory.flags),
            auth: { id: this.auth.id }
        }

        let isSuccessful = false;
        try {
            this.state.beginSave();

            const response = await this.noteService.saveNote(saveNote);
            isSuccessful = response.isSuccessful;
            this.infoUnsaved = !isSuccessful;  //if the response is successful we don't have unsaved changes
        }
        catch(error) {
            throw error;
        }
        finally {
            this.toaster.standard.saved('um:auth.additionalInfo.$long', isSuccessful);
            this.state.endSave();
        }
    }

    @apiCall
    protected async loadAdditionalInfo(): Promise<void> {

        if(!this.noteCategory?.id || !this.auth.id) { return; }

        try {
            this.state.beginSave();

            const response = await this.noteService.loadNotesByCategoryAndTarget(this.noteCategory.id, this.auth.id);
            //use the loaded note only if we don't have any changes
            if(!this.additionalInfo || this.additionalInfo?.text?.length === 0) {
                this.additionalInfo = response.results[0];
            }
            //compare the loaded note with the note in textbox and flag if there are unsaved changes
            if(this.additionalInfo && response.results[0].text === this.additionalInfo?.text) {
                this.infoUnsaved = false;
            }

        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endSave();
        }
    }

    @apiCall
    protected async loadNoteCategory(): Promise<void> {
        try {
            this.state.beginSave();

            const response = await this.noteService.loadCategoryByCode('ESSETTE:PP.UM.Auth.AdditionalInfo', {}, false, ['flags']);
            this.noteCategory = response.results[0];

        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endSave();
        }
    }

    @apiCall
    private async loadLookups(): Promise<void> {
        const loadSorting = new Sorting();
        loadSorting.add({
            reference: 'description',
            direction: SortDirection.Ascending
        });
        loadSorting.add({
            reference: 'code',
            direction: SortDirection.Ascending
        });

        const optionsSorted: Partial<IQueryLoadOptions> = {
            all: true,
            sorting: loadSorting
        };

        const fields = ['description'];
        const fieldsWithCode = ['description', 'code'];

        const loadPriorities = this.context.settings.priority.visible;
        const loadAdmissionSources = this.context.settings.admissionSource.visible;
        const loadHealthCareServiceTypes = this.context.settings.healthcareServiceType.visible;
        const loadPlacesOfService = this.context.settings.placeOfService.visible;

        try {
            this.state.beginLoad();
            const [priorities, admissionSources, healthcareServiceTypes, placesOfService] = await Promise.all([
                loadPriorities ? this.authService.loadAuthPriorities({ all: true }, undefined, fields) : null,
                loadAdmissionSources ? this.authService.loadAdmissionSources({ all: true }, undefined, fields) : null,
                loadHealthCareServiceTypes ? this.authService.loadHealthcareServiceTypes(optionsSorted, undefined, fieldsWithCode) : null,
                loadPlacesOfService ? this.lookupService.loadPlacesOfService(undefined, optionsSorted, undefined, fieldsWithCode) : null
            ]);

            this.priorities = priorities?.results || [];
            this.admissionSources = admissionSources?.results || [];
            this.healthcareServiceTypes = healthcareServiceTypes?.results ? naturalSort(healthcareServiceTypes.results, 'code') : [];
            this.placesOfService = placesOfService?.results ? naturalSort(placesOfService.results, 'code') : [];
        } catch(error) {
            this.logger.error('Error loading lookups', error);
            throw error;
        } finally {
            this.state.endLoad();
        }
    }


    @apiCall
    protected async saveAdditionalFields(saveAuth: UM.Auth | null = null): Promise<void> {
        if(!this.auth.id) return;

        let isMultiple = false;
        if(!saveAuth) {
            saveAuth = {
                id: this.auth.id
            };
            if(this.selectedPriority) saveAuth.priority = getReference(this.selectedPriority);
            if(this.selectedAdmissionSource) saveAuth.admission = { source: getReference(this.selectedAdmissionSource) };
            if(this.healthcareServiceTypes) saveAuth.healthcareServiceType = getReference(this.selectedHealthcareServiceType);
            if(this.placesOfService) saveAuth.placeOfService = getReference(this.selectedPlaceOfService);
            isMultiple = true;
        }

        let isSuccessful = false;
        // Update Auth
        try {
            this.state.beginLoad();
            const response = await this.authService.saveAuth(saveAuth);
            isSuccessful = response.isSuccessful;

        } catch(error) {
            this.logger.error('Error saving auth', error);
            throw error;
        } finally {
            this.state.endLoad();
            this.toaster.standard.saved(isMultiple ? 'pp:local.additionalFields' : 'pp:local.additionalField', isSuccessful, isMultiple ? 2 : 1);
            fireEvents(this.$element, ['auth-change'], { authReloaded: false, triggerRules: true });
        }
    }

    @apiCall
    protected async closeStepActions(): Promise<void> {
        try {
            this.state.beginSave();

            if(!this.auth.id) { return; }

            //await this.context.authDataComplete(this.auth.id);
            await this.context.triggerDeterminationRules();
            await this.context.refreshAuthSettings();
        } catch(error) {
            throw error
        } finally {
            this.state.endSave()
        }
    }

    @apiCall
    protected async savePreferredContact(): Promise<void> {
        const saveContact = this.preferredContactOption === 'existing' ? this.buildContactWithProviderContact() : this.buildContact();

        let isSuccessful = false;
        try {
            this.state.beginLoad();
            const response = await this.authService.saveAuthPreferredContact(saveContact);
            isSuccessful = response.isSuccessful;

            // Clear edited contact
            this.resetPreferredContact(true);
            fireEvents(this.$element, ['auth-change'], { authReloaded: false, triggerRules: false });
        } catch(error) {
            this.logger.error('Error saving auth', error);
            throw error;
        } finally {
            this.state.endLoad();
            this.toaster.standard.saved('um:authPreferredContact.$', isSuccessful);
        }
    }
}
